import { getCookie, removeCookie } from "./cookie";

export const oneIdPrefix = "oneId-";
const navigationPublishEvent = "navigation";

export const getUniversalAuthenticatorConfig = (brand, environment) => ({
	feature: `navigation-ui-${brand}`,
	environment,
});

export const getOneIdConfig = ({
	config,
	brandConfig,
	brand,
	appEnv,
	isCurrentPathPresent,
	localOneIdLibraryInstance,
	disableProfileLibrary,
	mapString,
	mapStringWithObject,
	isDevelopmentEnvironment,
	isCdnPage,
	authenticatorBundlePath,
	getCookie,
	store,
}) => {
	if (!config.skipOneId) {
		const oneIdConfig = {
			...config.profile.oneId,
			...brandConfig?.oneId,
			...config.environmentConfiguration?.default?.oneId,
			...config.environmentConfiguration?.[appEnv]?.oneId,
			...(config.environmentConfiguration?.[appEnv]?.oneId
				?.brandConfigurations?.[brand] || {}),
		};
		const oneIdEnv = oneIdConfig?.oneIdEnv;
		let usingOneIdLibrary = oneIdConfig?.usingOneIdLibrary
			? oneIdConfig.usingOneIdLibrary.includes(brand)
			: false;
		let usingOneIdProfileLibrary = oneIdConfig?.usingOneIdProfileLibrary
			? oneIdConfig.usingOneIdProfileLibrary.includes(brand)
			: false;
		const forceScriptLoadAndInitialization = isCurrentPathPresent(
			oneIdConfig.forceScriptLoadAndInitializationPages || []
		);
		if (usingOneIdLibrary || usingOneIdProfileLibrary) {
			if (localOneIdLibraryInstance === undefined) {
				localOneIdLibraryInstance = null;
				if (usingOneIdLibrary) {
					if (
						(oneIdConfig.preventScriptLoad ||
							(disableProfileLibrary !== undefined && disableProfileLibrary)) &&
						!forceScriptLoadAndInitialization
					) {
						localOneIdLibraryInstance = true;
					} else {
						localOneIdLibraryInstance = window?.OneID;
					}
				} else if (usingOneIdProfileLibrary && window?.PEP?.PROFILE_AUTH) {
					localOneIdLibraryInstance = window?.PEP?.PROFILE_AUTH;
				}
			}
			if (oneIdConfig.brandConfigurations?.[brand]?.includedPages) {
				const currentIsIncludedPage = isCurrentPathPresent(
					oneIdConfig.brandConfigurations?.[brand]?.includedPages
				);
				if (!currentIsIncludedPage) {
					usingOneIdLibrary = false;
					usingOneIdProfileLibrary = false;
				}
			} else if (oneIdConfig.brandConfigurations?.[brand]?.excludedPages) {
				const currentIsExcludedPage = isCurrentPathPresent(
					oneIdConfig.brandConfigurations?.[brand]?.excludedPages
				);
				usingOneIdLibrary = usingOneIdLibrary && !currentIsExcludedPage;
				usingOneIdProfileLibrary =
					usingOneIdProfileLibrary && !currentIsExcludedPage;
			}
			if (
				oneIdConfig.brandConfigurations?.[brand]
					?.excludedFromPreventInitializationPages
			) {
				if (
					isCurrentPathPresent(
						oneIdConfig.brandConfigurations?.[brand]
							?.excludedFromPreventInitializationPages
					)
				) {
					oneIdConfig.preventInitialization = false;
				}
			}
		}
		const isOneIdSource = usingOneIdLibrary || usingOneIdProfileLibrary;
		const oneIdClientId = isOneIdSource
			? mapString(oneIdConfig?.clientId, oneIdEnv)
			: "";
		const responderPath = oneIdConfig?.responderPagePath;
		const responderVersion = oneIdConfig?.responderVersion;
		const initializeOneId = () => {
			let oneIdProfileLibraryUrl;
			let oneIdResponderPage;
			if (oneIdConfig?.oneIdHostSupport && !isDevelopmentEnvironment()) {
				if (
					/(localhost)/i.test(window?.location.host) &&
					oneIdConfig?.localTestingAuthDomain
				) {
					oneIdProfileLibraryUrl = `${oneIdConfig.localTestingAuthDomain}${authenticatorBundlePath}`;
				} else {
					oneIdProfileLibraryUrl = usingOneIdProfileLibrary
						? "https://" + window?.location.host + authenticatorBundlePath
						: "";
				}
			} else {
				oneIdProfileLibraryUrl = usingOneIdProfileLibrary
					? mapString(
							oneIdConfig?.oneIdProfileLibrary,
							oneIdConfig?.oneIdProfileLibraryUrlPrefix
					  )
					: "";
			}
			oneIdResponderPage = isOneIdSource
				? mapStringWithObject(
						isCdnPage()
							? `${window?.location.pathname.replace("/index.html", "")}${
									config.environmentConfiguration.development?.oneId
										?.responderPage
							  }`
							: oneIdConfig?.responderPage,
						{
							responderVersion,
							oneIdEnv,
							oneIdClientId,
							origin: window?.location.origin,
							responderPath,
						}
				  )
				: "";
			return {
				oneIdProfileLibraryUrl,
				oneIdResponderPage,
			};
		};
		let shouldInitializeOneId = () =>
			!oneIdConfig?.disableForCookies?.some((cookie) => getCookie(cookie));
		let loadOneId =
			oneIdConfig?.enableForStore?.includes(store) || shouldInitializeOneId();
		// initializeOneId is only called once
		let oneIdProfileLibraryUrl;
		let oneIdResponderPage;
		const init = loadOneId ? initializeOneId() : null;
		oneIdProfileLibraryUrl = init?.oneIdProfileLibraryUrl;
		oneIdResponderPage = init?.oneIdResponderPage;
		const oneIdLibraryUrl = isOneIdSource
			? mapString(oneIdConfig?.oneIdLibrary, oneIdConfig?.oneIdLibraryUrlPrefix)
			: "";
		const disneyIDUrl = isOneIdSource
			? usingOneIdProfileLibrary
				? oneIdProfileLibraryUrl
				: oneIdLibraryUrl
			: "";
		return {
			...oneIdConfig,
			localOneIdLibraryInstance,
			forceScriptLoadAndInitialization,
			usingOneIdProfileLibrary,
			usingOneIdLibrary,
			isOneIdSource,
			oneIdClientId,
			oneIdResponderPage,
			oneIdLibraryUrl,
			disneyIDUrl,
		};
	}
};
export const publishNavigationEvent = (eventName, data = {}) => {
	document?.dispatchEvent(
		new CustomEvent(navigationPublishEvent, {
			detail: {
				event: eventName,
				data,
			},
		})
	);
};

export const publishOneIdError = (error) => {
	publishNavigationEvent(`${oneIdPrefix}error`, error);
};

export const getTravelAgentDataFromCookie = () => {
	const ticketDataJarCookie = getCookie("TICKET_DATA_jar");
	if (ticketDataJarCookie) {
		try {
			const travelAgentCookieData = JSON.parse(
				decodeURIComponent(ticketDataJarCookie)
			);
			if (
				travelAgentCookieData?.firstName ||
				travelAgentCookieData?.partnerId
			) {
				const {
					city,
					country,
					partnerId,
					partnerIdType,
					phone,
					addressLine1: line1,
					addressLine2: line2,
					agencyName: name,
					email: emailAddress,
					state: stateOrProvince,
					zipCode: postalCode,
					...rest
				} = travelAgentCookieData;
				return {
					emailAddress,
					travelAgency: {
						name,
						partnerId,
						partnerIdType,
						phone,
						address: {
							line1,
							line2,
							city,
							stateOrProvince,
							postalCode,
							country,
						},
					},
					...rest,
				};
			}
		} catch (error) {
			console.log(error);
		}
	}
	return null;
};

export const getNotificationsType = (brandConfig = {}, appEnv) => {
	let notificationsType = "default";
	if (brandConfig?.loadHeavyNotifications) {
		notificationsType = "heavy";
	}
	if (brandConfig?.useNotificationsCountEnvironments?.includes(appEnv)) {
		notificationsType = "count";
	}
	return notificationsType;
};

export const removeCartCookie = (brand, store) => {
	const cartCookieToRemove = `CART-${brand}${store ? `_${store}` : ""}_jar`;
	if (cartCookieToRemove) {
		removeCookie(cartCookieToRemove.split("=")[0]);
	}
};

export const removeOciCookies = () => {
	removeCookie("ociClientPassToken");
	removeCookie("ociReservationId");
};

/**
 * The `getNotifications` function processes and returns a structured object containing notification details used for
 * 												the NotificationsModalContent, MessageCountBadge components and Context
 *
 * @param {Object} notifications - The notifications information fetched from the profile API.
 * @param {boolean} isContext - A flag indicating whether to include the actual messages in the returned object.
 *                              This flag is essential to avoid storing sensitive information, such as messages,
 *                              in sessionStorage. Instead, when `isContext` is true, the function stores
 *                              these values in the context while setting user data.
 */
export const getNotifications = (
	notifications,
	isContext,
	loadHeavyNotificationsObject
) => {
	let notificationMessages = {
		itemCount: 0,
		receivedInvitations: { hasNotifications: false, messages: [] },
		potentialDuplicates: { hasNotifications: false, messages: [] },
		mergeRequests: { hasNotifications: false, messages: [] },
	};

	if (notifications) {
		if (loadHeavyNotificationsObject) {
			notificationMessages.itemCount = loadHeavyNotificationsObject.itemCount;
		} else if (notifications?.itemCount) {
			notificationMessages.itemCount = notifications?.itemCount;
		}

		if (
			notifications?.receivedInvitations?.length > 0 ||
			loadHeavyNotificationsObject?.messages?.receivedInvitations
				?.hasNotifications
		) {
			notificationMessages.receivedInvitations = {
				hasNotifications: true,
				messages: isContext ? notifications.receivedInvitations : [],
			};
		}

		if (
			notifications?.potentialDuplicates?.length > 0 ||
			loadHeavyNotificationsObject?.messages?.potentialDuplicates
				?.hasNotifications
		) {
			notificationMessages.potentialDuplicates = {
				hasNotifications: true,
				messages: isContext ? notifications.potentialDuplicates : [],
			};
		}

		if (
			notifications.mergeRequests?.length > 0 ||
			loadHeavyNotificationsObject?.messages?.mergeRequests?.hasNotifications
		) {
			notificationMessages.mergeRequests = {
				hasNotifications: true,
				messages: isContext ? notifications.mergeRequests : [],
			};
		}
	}

	return notificationMessages;
};

export const shouldUseUniversalAuth = ({
	locale,
	useUniversalAuthFlag,
	brand,
	envConfig,
}) => {
	const oneIdExcludedLocales =
		envConfig?.oneId?.brandConfigurations?.[brand]
			?.useUniversalAuthExcludedLocales || [];
	const isExcluded = oneIdExcludedLocales.includes(locale);
	if (isExcluded) return false;
	if (useUniversalAuthFlag !== null) return useUniversalAuthFlag === "true";
	return (
		envConfig?.oneId?.brandConfigurations?.[brand]?.useUniversalAuth ?? false
	);
};
